import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/errors";
import { GoogleG } from "../../components/GoogleG";
import { useBrandLogo } from "../../lib/logo";

export default function AdminLogin() {
  const { user, loading, loginWithPassword, loginWithGoogle } = useAuth();
  const { logoUrl } = useBrandLogo();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (user?.is_admin) return <Navigate to="/admin" replace />;
  if (user && !user.is_admin) return (
    <div className="min-h-screen flex items-center justify-center bg-[#3B2F2F] text-[#FDFBF7] flex-col gap-3">
      <div className="font-serif-display text-3xl">Not an admin account</div>
      <a href="/" className="text-[#D4AF37] underline text-sm">Back to store</a>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await loginWithPassword(email.trim(), password);
      if (!u.is_admin) { setErr("This account is not an admin. Contact your administrator."); return; }
      window.location.href = "/admin";
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#3B2F2F] text-[#FDFBF7] p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={logoUrl} alt="DailyBeans" className="w-20 h-20 mx-auto object-contain bg-[#FDFBF7] rounded-full p-2" />
          <div className="font-serif-display text-4xl mt-4">DailyBeans</div>
          <div className="uppercase tracking-[0.3em] text-xs text-[#D4AF37] mt-2">Admin Dashboard</div>
        </div>

        <form onSubmit={submit} className="bg-[#FDFBF7]/5 backdrop-blur rounded-3xl border border-white/10 p-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#D4AF37]">Email</label>
            <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="admin-login-email"
                   className="mt-1 w-full bg-white/5 border border-white/20 rounded-full px-4 py-3 outline-none text-[#FDFBF7] placeholder:text-[#FDFBF7]/40 focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#D4AF37]">Password</label>
            <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="admin-login-password"
                   className="mt-1 w-full bg-white/5 border border-white/20 rounded-full px-4 py-3 outline-none text-[#FDFBF7] focus:border-[#D4AF37]" />
          </div>
          {err && <div data-testid="admin-login-error" className="text-sm text-red-200 bg-red-900/30 border border-red-500/40 rounded-xl px-3 py-2">{err}</div>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit"
                  className="w-full bg-[#D4AF37] text-[#3B2F2F] rounded-full py-3 font-semibold hover:bg-[#c9a233] disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <Link to="/forgot-password" className="block text-center text-xs text-[#FDFBF7]/60 hover:text-[#D4AF37]">Forgot password?</Link>

          <div className="relative py-2 flex items-center gap-3 text-xs text-[#FDFBF7]/40">
            <div className="flex-1 border-t border-white/10"/> or <div className="flex-1 border-t border-white/10"/>
          </div>

          <button type="button" onClick={loginWithGoogle} data-testid="admin-login-google"
                  className="w-full border border-white/20 rounded-full py-3 text-sm font-medium text-[#FDFBF7] hover:bg-white/5 flex items-center justify-center gap-2">
            <GoogleG dark/> Continue with Google
          </button>
        </form>

        <div className="text-xs text-[#FDFBF7]/40 mt-6 text-center">The first account to sign up becomes admin.</div>
      </div>
    </div>
  );
}
